

import { useState } from "react";

interface ServiceFormData {
  name: string;
  description: string;
  selling_price: string;
  base_price: string;
}

interface ServiceFormProps {
  onSubmit: (data: any) => void;
  onCancel: () => void;
  initialData?: Partial<ServiceFormData>;
}

export default function ServiceForm({ onSubmit, onCancel, initialData }: ServiceFormProps) {
  const [formData, setFormData] = useState<ServiceFormData>({
    name: initialData?.name || "",
    description: initialData?.description || "",
    selling_price: initialData?.selling_price || "",
    base_price: initialData?.base_price || "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof ServiceFormData, string>>>({});

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ServiceFormData, string>> = {};
    
    if (!formData.name.trim()) newErrors.name = "Service name is required";
    if (!formData.selling_price.trim()) newErrors.selling_price = "Selling price is required";
    if (!formData.base_price.trim()) newErrors.base_price = "Base price is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        name: formData.name,
        description: formData.description,
        selling_price: parseFloat(formData.selling_price) || 0,
        base_price: parseFloat(formData.base_price) || 0,
      });
    }
  };

  const handleChange = (field: keyof ServiceFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const inputClass = (field: keyof ServiceFormData) => `
    w-full px-3 py-2.5 rounded-xl border text-sm transition-colors
    ${errors[field] 
      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100" 
      : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
    }
    outline-none
  `;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4">
        {/* Service Name */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Service Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Enter service name"
            className={inputClass("name")}
          />
          {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            placeholder="Service description"
            rows={2}
            className={`${inputClass("description")} resize-none`}
          />
        </div>

        {/* Base Price and Selling Price - 2 Column */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Base Price (Cost) *</label>
            <input
              type="number"
              value={formData.base_price}
              onChange={(e) => handleChange("base_price", e.target.value)}
              placeholder="0.00"
              className={inputClass("base_price")}
            />
            {errors.base_price && <p className="text-xs text-rose-500 mt-1">{errors.base_price}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Selling Price *</label>
            <input
              type="number"
              value={formData.selling_price}
              onChange={(e) => handleChange("selling_price", e.target.value)}
              placeholder="0.00"
              className={inputClass("selling_price")}
            />
            {errors.selling_price && <p className="text-xs text-rose-500 mt-1">{errors.selling_price}</p>}
          </div>
        </div>

        {/* Commission Preview */}
        {formData.selling_price && formData.base_price && (
          <div className="bg-amber-50 rounded-xl p-3">
            <div className="text-xs text-stone-500">Your Commission</div>
            <div className="text-lg font-bold text-amber-700">
              ₹{((parseFloat(formData.selling_price) || 0) - (parseFloat(formData.base_price) || 0)).toLocaleString()}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-stone-100">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData ? "Update Service" : "Add Service"}
        </button>
      </div>
    </form>
  );
}
